import {
  Stack,
  useBreakpointValue,
  Box,
  useToast,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  ModalCloseButton,
} from "@chakra-ui/react";
import React from "react";
import { useButtonProps, useUserProfile } from "hooks";
import { useDispatch } from "react-redux";
import { ToastMessageContent } from "screens/common/components";
import { purchaseDeal } from "api/billingPlan";
import type { DealFinderTicker } from "../../../../api/tickers/models/DealFinderTicker";
import { PurchasingCartForm } from "./PurchasingCartForm";
import { useCopyValue } from "hooks/useCopies";
import { updateCustomerDetails } from "state/customerDetails/operations";
import { track } from "api/analytics";
import {
  DEAL_FINDER_DOWNLOAD_REPORT,
  DEAL_FINDER_PAYMENT_FAILED,
  DEAL_FINDER_PAYMENT_SUCCEEDED,
  DEAL_FINDER_PROCESSING_REPORT,
} from "api/analytics/events";
import type { AvailableForPurchase } from "api/billingPlan/models/AvailableForPurchase";
import type { ComponentInfo } from "api/billingPlan/models/ComponentInfo";

interface PurchasingCartConfirmProps {
  deal: DealFinderTicker;
  selectedProductKey: string;
  selectedProductOption: ComponentInfo & AvailableForPurchase;
  onClose: (success?: boolean) => void;
}

export const PurchasingCartConfirm: React.FC<PurchasingCartConfirmProps> = ({
  deal,
  selectedProductKey,
  selectedProductOption,
  onClose,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const { isOpen, onOpen, onClose: onModalClose } = useDisclosure();
  const [downloadUrl, setDownloadUrl] = React.useState<string | undefined>(undefined);
  const { fullName, email, id: userId } = useUserProfile();

  const onPurchaseFormSubmit = async (fields: {
    firstName: string;
    lastName: string;
    organization: string;
    city: string;
    address: string;
    address2: string;
    country: string;
    state: string;
    postcode: string;
    acceptTerms: boolean;
    creditCardToken?: string;
    chargifyGatewayHandle?: string;
  }) => {
    const { firstName, lastName, address, address2, country, state, postcode, city, creditCardToken, chargifyGatewayHandle } = fields;

    const customerDetails = {
      firstName,
      lastName,
      address,
      address2,
      country,
      state,
      city,
      zip: postcode,
    };

    // Update customer details through Redux thunk
    dispatch(
      updateCustomerDetails({
        userId: String(userId),
        details: customerDetails,
      })
    );

    // Only include credit card details if both token and gateway handle are present
    const creditCardDetails =
      creditCardToken && chargifyGatewayHandle
        ? {
            token: creditCardToken,
            gatewayHandle: chargifyGatewayHandle,
          }
        : undefined;

    try {
      const result = await purchaseDeal(String(userId), deal.id, selectedProductKey, creditCardDetails);
      track(DEAL_FINDER_PAYMENT_SUCCEEDED, {
        dealName: deal?.name,
        dealExchange: deal?.exchange,
        dealTicker: deal?.symbol,
        reportType: selectedProductKey,
        userName: fullName,
        userEmail: email,
      });
      if (result.status === "purchased" && "downloadUrl" in result && result.downloadUrl) {
        setDownloadUrl(result.downloadUrl);
      }
      onOpen();
    } catch (error) {
      toast.closeAll();
      track(DEAL_FINDER_PAYMENT_FAILED, {
        dealName: deal?.name,
        dealExchange: deal?.exchange,
        dealTicker: deal?.symbol,
        reportType: selectedProductKey,
        userName: fullName,
        userEmail: email,
      });
      toast({
        render: ({ onClose }) => (
          <ToastMessageContent
            message={"We apologize, we are unable to process your purchase at this time. Please contact support or try again later"}
            onClick={() => {
              onClose();
            }}
            backgroundColor={"red.200"}
            onClose={onClose}
          />
        ),
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  return (
    <Stack justifyContent={"space-between"} height="100%" minHeight="unset" spacing="0" width="100%">
      <SuccessModal
        isOpen={isOpen}
        onClose={() => {
          window.dispatchEvent(new CustomEvent("refreshPurchases"));
          onModalClose();
          onClose(true);
        }}
        downloadUrl={downloadUrl}
        deal={deal}
        selectedProductKey={selectedProductKey}
      />
      <Stack spacing="0" height="100%" justifyContent={"space-between"} color={textColor}>
        <Box height="100%" p={isMobile ? "0.5rem" : "2rem"} pt={isMobile ? "0rem" : "2rem"}>
          <PurchasingCartForm
            onSubmit={onPurchaseFormSubmit}
            onClose={onClose}
            deal={deal}
            selectedProductKey={selectedProductKey}
            productName={selectedProductOption.componentName}
            productPrice={selectedProductOption.price}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

const SuccessModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  downloadUrl?: string;
  deal: DealFinderTicker;
  selectedProductKey: string;
}> = ({ isOpen, onClose, downloadUrl, deal, selectedProductKey }) => {
  const reportAvailable = useCopyValue("copy_purchase_report_available_immediate");
  const reportLater = useCopyValue("copy_purchase_report_available_later");
  const primaryButtonProps = useButtonProps("sm", "cta");
  const { fullName, email } = useUserProfile();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnEsc closeOnOverlayClick>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Purchase Confirmed</ModalHeader>
        <ModalCloseButton color={"gray.400"} _hover={{}} />
        <ModalBody fontSize={"sm"} py="0">
          {downloadUrl ? reportAvailable : reportLater}
        </ModalBody>
        <ModalFooter>
          <Stack justifyContent={"flex-end"} width="100%" direction="row">
            {downloadUrl ? (
              <Button
                {...primaryButtonProps}
                onClick={() => {
                  track(DEAL_FINDER_DOWNLOAD_REPORT, {
                    dealName: deal?.name,
                    dealExchange: deal?.exchange,
                    dealTicker: deal?.symbol,
                    reportType: selectedProductKey,
                    userName: fullName,
                    userEmail: email,
                  });

                  window.open(downloadUrl || "", "_blank");
                }}>
                Download PDF
              </Button>
            ) : (
              <Button
                {...primaryButtonProps}
                onClick={() => {
                  track(DEAL_FINDER_PROCESSING_REPORT, {
                    dealName: deal?.name,
                    dealExchange: deal?.exchange,
                    dealTicker: deal?.symbol,
                    reportType: selectedProductKey,
                    userName: fullName,
                    userEmail: email,
                  });

                  onClose();
                }}>
                Close
              </Button>
            )}
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
